<template>
  <div class="lv-pdf-viewer__wrap">
    <div v-if="isRendered" class="lv-pdf-viewer__zoom-controls">
      <button
        type="button"
        class="lv-pdf-viewer__zoom-btn"
        :disabled="scale <= scaleMin"
        @click="$_zoomOut"
      >
        <i class="icon-reduce" />
      </button>
      <button
        type="button"
        class="lv-pdf-viewer__zoom-btn"
        :disabled="scale >= scaleMax"
        @click="$_zoomIn"
      >
        <i class="icon-add" />
      </button>
    </div>
    <div ref="scrollContainer" class="lv-pdf-viewer" :class="{ 'render-failed': renderFailed }">
      <!-- 加密 PDF：顯示密碼輸入表單 -->
      <div v-if="needPassword" class="lv-pdf-viewer__password-form">
        <p class="lv-pdf-viewer__password-title">{{ $t('輸入') }}</p>
        <p v-if="passwordError" class="lv-pdf-viewer__password-error">{{ passwordError }}</p>
        <input
          v-model="passwordInput"
          type="password"
          class="lv-pdf-viewer__password-input"
          placeholder="輸入"
          autocomplete="off"
          @keyup.enter="$_submitPassword"
        />
        <button
          type="button"
          class="lv-pdf-viewer__password-btn"
          :disabled="!passwordInput.trim()"
          @click="$_submitPassword"
        >
          {{ $t('確定') }}
        </button>
      </div>
      <div v-else-if="renderFailed" class="lv-pdf-viewer__no-preview">
        <LvText h3 bold>{{ $t('此檔案無法預覽') }}</LvText>
        <a download class="lv-pdf-viewer__download-link">
          <WsButton001 leading-icon="icon-cloud-download" @click="$_onDownload">
            {{ $t('下載檔案') }}
          </WsButton001>
        </a>
      </div>
      <div v-else class="lv-pdf-viewer__body">
        <LvLoading v-if="!isRendered" />
        <div v-show="isRendered" ref="container" class="lv-pdf-viewer__pages" />
        <LvLoading v-if="isRendered && isLoadingMore" class="lv-pdf-viewer__loading-more" />
      </div>
    </div>
  </div>
</template>

<script>
import * as pdfjsLib from 'pdfjs-dist/build/pdf';

const { PasswordResponses } = pdfjsLib;

// 使用 build 時複製到輸出的本地 worker（見 vue.config.js CopyPlugin）
pdfjsLib.GlobalWorkerOptions.workerSrc = (process.env.BASE_URL || '/') + 'pdf.worker.min.js';

export default {
  props: {
    url: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      pdfDoc: null,
      needPassword: false,
      passwordError: '',
      passwordInput: '',
      passwordUpdatePassword: null,
      passwordResolve: null,
      isRendered: false,
      renderFailed: false,
      scale: 1,
      scaleMin: 0.5,
      scaleMax: 3,
      totalPages: 0,
      loadedPageCount: 0,
      isLoadingMore: false,
      scrollListener: null,
    };
  },
  watch: {
    url: {
      handler() {
        this.$_loadPdf();
      },
      immediate: false,
    },
  },
  mounted() {
    this.$_loadPdf();
  },
  beforeDestroy() {
    this.$_removeScrollListener();
    if (this.pdfDoc) {
      this.pdfDoc.destroy();
      this.pdfDoc = null;
    }
  },
  methods: {
    async $_loadPdf() {
      if (!this.url) return;

      if (this.pdfDoc) {
        this.pdfDoc.destroy();
        this.pdfDoc = null;
      }

      this.needPassword = false;
      this.passwordError = '';
      this.passwordInput = '';
      this.isRendered = false;
      this.renderFailed = false;
      this.loadedPageCount = 0;
      this.totalPages = 0;
      this.$_removeScrollListener();

      await this.$nextTick();
      const container = this.$refs.container;
      if (container) {
        container.innerHTML = '';
      }

      try {
        const loadingTask = pdfjsLib.getDocument(this.url);

        // 加密 PDF 時 pdf.js 會呼叫 onPassword，需回傳 Promise 並在使用者輸入密碼後 resolve
        loadingTask.onPassword = (updatePassword, reason) => {
          return new Promise(resolve => {
            this.needPassword = true;
            this.passwordError = reason === PasswordResponses.INCORRECT_PASSWORD ? '錯誤' : '';
            this.passwordUpdatePassword = updatePassword;
            this.passwordResolve = resolve;
          });
        };

        this.pdfDoc = await loadingTask.promise;
        this.totalPages = this.pdfDoc.numPages;
        this.loadedPageCount = 0;

        await this.$_renderPages();
        this.$_renderedHandler();
        this.$_setupScrollListener();
      } catch (err) {
        this.$_errorHandler(err);
        this.renderFailed = true;
      }
    },
    $_submitPassword() {
      const pwd = this.passwordInput.trim();
      if (!pwd || !this.passwordUpdatePassword || !this.passwordResolve) return;

      this.passwordUpdatePassword(pwd);
      this.passwordResolve();
      this.needPassword = false;
      this.passwordError = '';
      this.passwordInput = '';
      this.passwordUpdatePassword = null;
      this.passwordResolve = null;
    },
    async $_renderPages() {
      const pagesContainer = this.$refs.container;
      if (!pagesContainer || !this.pdfDoc) return;

      const isInitial = this.loadedPageCount === 0;
      const isZoom = !isInitial;

      if (isInitial) {
        pagesContainer.innerHTML = '';
        const toLoad = Math.min(5, this.totalPages);
        for (let pageNum = 1; pageNum <= toLoad; pageNum += 1) {
          await this.$_renderOnePage(pagesContainer, pageNum);
        }
        this.loadedPageCount = toLoad;
      } else if (isZoom) {
        pagesContainer.innerHTML = '';
        for (let pageNum = 1; pageNum <= this.loadedPageCount; pageNum += 1) {
          await this.$_renderOnePage(pagesContainer, pageNum);
        }
      }
    },
    async $_renderOnePage(pagesContainer, pageNum) {
      const page = await this.pdfDoc.getPage(pageNum);
      const scaledViewport = page.getViewport({ scale: this.scale });

      const wrapper = document.createElement('div');
      wrapper.className = 'lv-pdf-viewer__page-wrapper';

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = scaledViewport.height;
      canvas.width = scaledViewport.width;

      wrapper.appendChild(canvas);
      pagesContainer.appendChild(wrapper);

      await page.render({
        canvasContext: context,
        viewport: scaledViewport,
      }).promise;
    },
    $_setupScrollListener() {
      this.$_removeScrollListener();
      const el = this.$refs.scrollContainer;
      if (!el) return;
      this.scrollListener = () => this.$_onScroll();
      el.addEventListener('scroll', this.scrollListener, { passive: true });
    },
    $_removeScrollListener() {
      const el = this.$refs.scrollContainer;
      if (el && this.scrollListener) {
        el.removeEventListener('scroll', this.scrollListener);
        this.scrollListener = null;
      }
    },
    async $_onScroll() {
      const el = this.$refs.scrollContainer;
      if (!el || this.isLoadingMore || this.loadedPageCount >= this.totalPages) return;
      const threshold = 100;
      const { scrollTop, clientHeight, scrollHeight } = el;
      if (scrollTop + clientHeight < scrollHeight - threshold) return;

      this.isLoadingMore = true;
      const pagesContainer = this.$refs.container;
      if (!pagesContainer || !this.pdfDoc) {
        this.isLoadingMore = false;
        return;
      }
      const nextStart = this.loadedPageCount + 1;
      const nextEnd = Math.min(this.loadedPageCount + 5, this.totalPages);
      for (let pageNum = nextStart; pageNum <= nextEnd; pageNum += 1) {
        await this.$_renderOnePage(pagesContainer, pageNum);
      }
      this.loadedPageCount = nextEnd;
      this.isLoadingMore = false;
    },
    $_zoomIn() {
      if (this.scale >= this.scaleMax) return;
      this.scale = Math.min(this.scale + 0.25, this.scaleMax);
      this.$_renderPages();
    },
    $_zoomOut() {
      if (this.scale <= this.scaleMin) return;
      this.scale = Math.max(this.scale - 0.25, this.scaleMin);
      this.$_renderPages();
    },
    $_renderedHandler() {
      this.isRendered = true;
    },
    $_errorHandler(err) {
      console.error('render failed', err);
    },
    $_onDownload() {
      this.$emit('download', this.url);
    },
  },
};
</script>
